import type {
  CountRow,
  TagRecord,
  MeasurementBreakdownTargetRef,
  MeasurementBreakdownTargetRow,
} from "@/types/takeoff-pilot";
import { formatMeasurementValue, roundMeasurementValue, type MeasurementType } from "@/lib/geometry";
import { MEASUREMENT_TYPES } from "@/lib/measurement-domain";
import { toCSV, downloadFile } from "@/lib/csv";
import {
  TAKEOFF_COST_BASIS,
  type TakeoffCostBasis,
} from "@/lib/takeoff-domain";

export const UNIT_COST_DECIMAL_PATTERN = /^\d+(\.\d{1,2})?$/;

export function getEffectiveCostBasis(
  costBasis?: TakeoffCostBasis
): TakeoffCostBasis {
  return costBasis ?? TAKEOFF_COST_BASIS.measurement;
}

export function getMeasurementQuantity(result?: Pick<CountRow, "lf" | "sqft" | "cf">): number {
  if (!result) return 0;
  if (result.lf > 0) return roundMeasurementValue(result.lf, MEASUREMENT_TYPES.LF);
  if (result.sqft > 0) return roundMeasurementValue(result.sqft, MEASUREMENT_TYPES.SQFT);
  if (result.cf > 0) return roundMeasurementValue(result.cf, MEASUREMENT_TYPES.CF);
  return 0;
}

export function getTotalCost(
  result: Pick<CountRow, "count" | "lf" | "sqft" | "cf"> | undefined,
  unitCost?: number | null,
  costBasis?: TakeoffCostBasis
): number | null {
  if (unitCost === null || unitCost === undefined) {
    return null;
  }
  const effectiveCostBasis = getEffectiveCostBasis(costBasis);
  const quantity =
    effectiveCostBasis === TAKEOFF_COST_BASIS.count
      ? (result?.count ?? 0)
      : getMeasurementQuantity(result);
  return unitCost * quantity;
}

export function formatCurrency(value: number): string {
  return value.toFixed(2);
}

export function formatMeasurementTotal(
  measurementType: MeasurementType,
  value: number | null | undefined
): string {
  if (value === null || value === undefined) {
    return "—";
  }
  return formatMeasurementValue(value, measurementType);
}

export function formatMeasurementTotalForExport(
  measurementType: MeasurementType,
  value: number | null | undefined
): string {
  if (value === null || value === undefined) {
    return "";
  }
  return formatMeasurementValue(value, measurementType);
}

export type MeasurementBreakdownRow = {
  measurementType: MeasurementType;
  value: number;
  count: number;
};

export type GroupedMeasurementBreakdownRow = {
  measurementType: MeasurementType;
  displayValue: string;
  count: number;
  sortValue: number;
  targets: MeasurementBreakdownTargetRef[];
};

const MEASUREMENT_TYPE_ORDER: Record<MeasurementType, number> = {
  [MEASUREMENT_TYPES.LF]: 0,
  [MEASUREMENT_TYPES.SQFT]: 1,
  [MEASUREMENT_TYPES.CF]: 2,
};

const MEASUREMENT_EXPORT_COLUMNS = [
  MEASUREMENT_TYPES.LF,
  MEASUREMENT_TYPES.SQFT,
  MEASUREMENT_TYPES.CF,
] as const;

export function groupMeasurementBreakdownByDisplayValue(
  breakdown: MeasurementBreakdownRow[] | undefined,
  breakdownTargets: MeasurementBreakdownTargetRow[] | undefined
): GroupedMeasurementBreakdownRow[] {
  const grouped = new Map<string, GroupedMeasurementBreakdownRow>();
  for (const row of breakdown ?? []) {
    const displayValue = formatMeasurementValue(row.value, row.measurementType);
    const roundedSortValue = roundMeasurementValue(row.value, row.measurementType);
    const key = `${row.measurementType}:${displayValue}`;
    const existing = grouped.get(key);
    if (existing) {
      existing.count += row.count;
      existing.sortValue = Math.max(existing.sortValue, roundedSortValue);
      continue;
    }
    grouped.set(key, {
      measurementType: row.measurementType,
      displayValue,
      count: row.count,
      sortValue: roundedSortValue,
      targets: [],
    });
  }

  for (const row of breakdownTargets ?? []) {
    const displayValue = formatMeasurementValue(row.value, row.measurementType);
    const roundedSortValue = roundMeasurementValue(row.value, row.measurementType);
    const key = `${row.measurementType}:${displayValue}`;
    const existing = grouped.get(key);
    if (!existing) {
      grouped.set(key, {
        measurementType: row.measurementType,
        displayValue,
        count: row.count,
        sortValue: roundedSortValue,
        targets: [...row.targets],
      });
      continue;
    }
    const existingTargetIds = new Set(existing.targets.map((target) => String(target.measurementId)));
    for (const target of row.targets) {
      const targetId = String(target.measurementId);
      if (existingTargetIds.has(targetId)) {
        continue;
      }
      existingTargetIds.add(targetId);
      existing.targets.push(target);
    }
    existing.sortValue = Math.max(existing.sortValue, roundedSortValue);
  }

  for (const row of grouped.values()) {
    row.targets.sort(
      (left, right) =>
        String(left.planDocumentId).localeCompare(String(right.planDocumentId)) ||
        left.pageNumber - right.pageNumber ||
        String(left.measurementId).localeCompare(String(right.measurementId))
    );
  }

  return Array.from(grouped.values()).sort(
    (left, right) =>
      MEASUREMENT_TYPE_ORDER[left.measurementType] - MEASUREMENT_TYPE_ORDER[right.measurementType] ||
      right.sortValue - left.sortValue
  );
}

export type DownloadWorkbookOptions = {
  details?: { job?: { name?: string } };
  isCountsPending: boolean;
  countRows: CountRow[] | undefined;
  isMeasurementTotalsInitialLoad: boolean;
  hasMeasurementTotalsError: boolean;
  selectedTagsForExport: TagRecord[];
  selectedResults: CountRow[];
};

export function downloadTakeoffWorkbook(args: DownloadWorkbookOptions): string | null {
  if (args.isCountsPending || !args.countRows) {
    return "Tag counts are still loading";
  }
  if (args.isMeasurementTotalsInitialLoad) {
    return "Measurement totals are still loading";
  }
  if (args.hasMeasurementTotalsError) {
    return "Measurement totals are unavailable";
  }
  if (args.selectedTagsForExport.length === 0) {
    return "Select at least one tag to download";
  }

  const resultsByTagId = new Map(
    args.selectedResults.map((row) => [row.tagId, row] as const)
  );
  const csvContent = toCSV(
    ["Tag", "Count", ...MEASUREMENT_EXPORT_COLUMNS, "Pricing Basis", "Unit Cost", "Total Cost"],
    args.selectedTagsForExport.map((tag) => {
      const result = resultsByTagId.get(tag._id);
      const totalCost = getTotalCost(result, tag.unitCost, tag.costBasis);

      return [
        tag.rawValue,
        result ? String(result.count) : "",
        formatMeasurementTotalForExport(MEASUREMENT_TYPES.LF, result?.lf),
        formatMeasurementTotalForExport(MEASUREMENT_TYPES.SQFT, result?.sqft),
        formatMeasurementTotalForExport(MEASUREMENT_TYPES.CF, result?.cf),
        getEffectiveCostBasis(tag.costBasis) === TAKEOFF_COST_BASIS.count
          ? "Count"
          : "Measurement",
        tag.unitCost == null ? "" : formatCurrency(tag.unitCost),
        totalCost == null ? "" : formatCurrency(totalCost),
      ];
    })
  );

  const safeJobName =
    (args.details?.job?.name ?? "takeoff")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "takeoff";
  downloadFile(csvContent, `${safeJobName}-takeoff-results.csv`, "text/csv;charset=utf-8;");
  return null;
}
